import React, { ReactElement } from 'react';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart, registerables } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import FlexContainer from '../flex-container/flex-container.component';
import { ACCENT_COLORS_SHORT, COLORS } from '../../constants/system/colors';

const ChartStackSingle: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const CHART_STACK_HEIGHT: number = 280;
  const CHART_STACK_WIDTH: number = 200;
  const CHART_STACK_PADDING_TOP: number = 10;
  const CHART_STACK_PADDING_BOTTOM: number = 10;
  const CHART_STACK_PADDING_LEFT: number = 10;
  const CHART_STACK_BORDER_WIDTH: number = 1;
  const CHART_STACK_BAR_MAX_WIDTH: number = 150;
  const CHART_STACK_LABEL_ALIGN_LEFT: ChartLabelAlignment = 'left';
  const CHART_STACK_LABEL_OFFSET: number = 10;
  const CHART_STACK_LABEL_FONT_WEIGHT_BOLD: ChartLabelFontStyle = 'bold';

  const {
    containerId,
    chartItemList,
    label,
    labelFormatter,
  } = props;

  const chartStackSingleLabelList: string[] = chartItemList
    .map((chartItem: ChartStackSingleItem): string => chartItem.name).reverse();
  const chartStackSingleValueList: number[] = chartItemList
    .map((chartItem: ChartStackSingleItem): number => chartItem.value).reverse();

  Chart.register(...registerables);

  return (
    <FlexContainer
      isCentered
      id={containerId}
      style={{
        height: CHART_STACK_HEIGHT,
        width: CHART_STACK_WIDTH,
        paddingTop: CHART_STACK_PADDING_TOP,
        paddingBottom: CHART_STACK_PADDING_BOTTOM,
        paddingLeft: CHART_STACK_PADDING_LEFT,
      }}
    >
      {
        chartItemList && !!chartItemList.length && (
          <Bar
            id={containerId}
            height={CHART_STACK_HEIGHT}
            width={CHART_STACK_WIDTH}
            data={{
              labels: [label],
              datasets: chartStackSingleValueList.map((
                chartStackSingleValue: number, chartStackSingleValueIndex: number,
              ): ChartDataset => ({
                label: chartStackSingleLabelList[chartStackSingleValueIndex],
                data: [chartStackSingleValue],
                backgroundColor: ACCENT_COLORS_SHORT[chartStackSingleValueIndex],
                borderWidth: CHART_STACK_BORDER_WIDTH,
                maxBarThickness: CHART_STACK_BAR_MAX_WIDTH,
              })),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  stacked: true,
                  display: false,
                  grid: {
                    display: false,
                  },
                },
                y: {
                  stacked: true,
                  ticks: {
                    callback: (axisLabel: string | number): string => {
                      if (labelFormatter) {
                        return labelFormatter(axisLabel.toString());
                      }

                      return axisLabel.toString();
                    },
                  },
                  grid: {
                    display: false,
                  },
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: (context: ChartInstance): string => {
                      return ` ${context.dataset.label}`;
                    },
                  },
                },
                datalabels: {
                  color: COLORS.white,
                  align: CHART_STACK_LABEL_ALIGN_LEFT,
                  formatter: (value: number): string => {
                    if (labelFormatter) {
                      return ` ${labelFormatter(value)}`;
                    }

                    return ` ${value.toString()}`;
                  },
                  offset: CHART_STACK_LABEL_OFFSET,
                  labels: {
                    value: {
                      font: {
                        weight: CHART_STACK_LABEL_FONT_WEIGHT_BOLD,
                      },
                    },
                  },
                },
              },
            }}
            plugins={[ChartDataLabels]}
          />
        )
      }
    </FlexContainer>
  );
});

export default ChartStackSingle;
